import { ILineFare } from "./../domain/entities/lineInfo/lineInfo.types";
import {
  IBookableDatesInfo,
  IBookInfo,
  IBookResultInfo,
  ITempBookResult,
  IDateInfo,
  IKakaoPayBookInfo,
} from "@/domain/entities/book";
import { ISeatInfo } from "@/domain/entities/book";
import { IResultData } from "@/domain/entities/shared/result";
import {
  GetSeatLayoutUseCase,
  ISeatLayoutResult,
} from "@/domain/usecases/book/GetSeatLayout";
import { HasFullyBookedDateUseCase } from "@/domain/usecases/book/HasFullyBookedDate";
import { BookableDatesInfoToDateInfosUseCase } from "@/domain/usecases/book/BookableDatesInfoToDateInfos";
import { GetBookableDatesFactory } from "@/domain/usecases/book/factory/GetBookableDatesFactory";
import TYPES from "@/ioc/identifiers";
import { container } from "@/ioc/inversify.config";
import { IBookRepository } from "@/repositories/book/bookRepository.types";
import { BookHistoryDateCriteria, BookIntervalCode } from "@/types";
import useSetResultCodeIfNeeded from "@/use/useSetResultCodeIfNeeded";
import { defineStore } from "pinia";

interface IBookState {
  seatsInfos: ISeatInfo[][][];
  bookInfos: IBookInfo[];
  bookResultInfo: IBookResultInfo | undefined;
  completedBookInfos: IBookInfo[] | undefined;
  errorMessage: string | undefined;
}

const bookRepository = () =>
  container.get<IBookRepository>(TYPES.IBookRepository);

const getBookableDatesFactory = () =>
  container.get<GetBookableDatesFactory>(TYPES.GetBookableDatesFactory);

const getSeatLayout = () =>
  container.get<GetSeatLayoutUseCase>(TYPES.GetSeatLayoutUseCase);

const hasFullyBookedDate = container.get<HasFullyBookedDateUseCase>(
  TYPES.HasFullyBookedDateUseCase
);

const bookableDatesInfoToDateInfos =
  container.get<BookableDatesInfoToDateInfosUseCase>(
    TYPES.BookableDatesInfoToDateInfosUseCase
  );

export const useBookStore = defineStore("book", {
  state: (): IBookState => {
    return {
      seatsInfos: [],
      bookInfos: [],
      bookResultInfo: undefined,
      completedBookInfos: undefined,
      errorMessage: undefined,
    };
  },
  getters: {},
  actions: {
    async getBookableDates(
      turnId: string,
      getOnBusStopId: string,
      getOffBusStopId: string,
      bookIntervalCode: BookIntervalCode,
      lineFare?: ILineFare,
      targetSeatNo?: string
    ): Promise<IResultData<IBookableDatesInfo[]>> {
      const getBookableDates = getBookableDatesFactory().create(
        bookIntervalCode,
        lineFare?.dailyFare,
        lineFare?.monthlyFare
      );

      return getBookableDates.execute(
        turnId,
        getOnBusStopId,
        getOffBusStopId,
        false,
        targetSeatNo
      );
    },

    async getEarliestBookableDate(
      turnId: string,
      getOnBusStopId: string,
      getOffBusStopId: string,
      bookIntervalCode: BookIntervalCode,
      fetchLatest = false
    ): Promise<IBookableDatesInfo | null> {
      const getBookableDates =
        getBookableDatesFactory().create(bookIntervalCode);
      const bookableDates = await getBookableDates.execute(
        turnId,
        getOnBusStopId,
        getOffBusStopId,
        fetchLatest
      );

      return bookableDates.data?.[0] ?? null;
    },

    hasFullyBookedDate(bookableDates: IBookableDatesInfo[]): boolean {
      return hasFullyBookedDate.execute(bookableDates);
    },

    async getSeatLayout(
      turnId: string,
      date: string,
      isMonthlyBook: boolean,
      allocUid?: string
    ): Promise<IResultData<ISeatLayoutResult>> {
      return await getSeatLayout().execute(
        turnId,
        date,
        isMonthlyBook,
        allocUid
      );
    },

    async getBookInfos(
      bookStatusCodesStr: string,
      sort: string,
      fromDateCriteria?: BookHistoryDateCriteria,
      fromDate?: string,
      toDateCriteria?: BookHistoryDateCriteria,
      toDate?: string
    ): Promise<IResultData<IBookInfo[]>> {
      const bookInfosResult: IResultData<IBookInfo[]> = {
        isSuccess: false,
        data: [],
      };

      await bookRepository()
        .getBookInfos(
          bookStatusCodesStr,
          sort,
          fromDateCriteria,
          fromDate,
          toDateCriteria,
          toDate
        )
        .then((result) => {
          result
            .map((result) => {
              bookInfosResult.isSuccess = true;
              bookInfosResult.data = result;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(bookInfosResult, error);
              bookInfosResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          bookInfosResult.errorMessage = error.message;
        });

      this.bookInfos = bookInfosResult.data ?? [];
      return bookInfosResult;
    },

    async getBookResultInfo(
      bookUid: string
    ): Promise<IResultData<IBookResultInfo>> {
      const bookResultInfoResult: IResultData<IBookResultInfo> = {
        isSuccess: false,
      };

      await bookRepository()
        .getBookResultInfo(bookUid)
        .then((result) => {
          result
            .map((result) => {
              bookResultInfoResult.isSuccess = true;
              bookResultInfoResult.data = result;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(bookResultInfoResult, error);
              bookResultInfoResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          bookResultInfoResult.errorMessage = error.message;
        });

      this.bookResultInfo = bookResultInfoResult.data;
      return bookResultInfoResult;
    },

    convertBookableDatesInfoToDateInfos(
      bookableDatesInfo: IBookableDatesInfo
    ): IDateInfo[] {
      return bookableDatesInfoToDateInfos.execute(bookableDatesInfo);
    },

    async getBookInfoDetail(
      bookId: string,
      bookDetailId: string
    ): Promise<IResultData<IBookInfo>> {
      const bookInfoResult: IResultData<IBookInfo> = {
        isSuccess: false,
      };

      await bookRepository()
        .getBookInfoDetail(bookId, bookDetailId)
        .then((result) => {
          result
            .map((result) => {
              bookInfoResult.isSuccess = true;
              bookInfoResult.data = result;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(bookInfoResult, error);
              bookInfoResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          bookInfoResult.errorMessage = error.message;
        });
      return bookInfoResult;
    },

    async requestKakaoPay(
      autoSeatAssgin: boolean,
      seatsSelectable: boolean,
      turnId: string,
      getOnStationId: string,
      getOffStationId: string,
      dateInfos: IDateInfo[],
      isMonthlyBook: boolean
    ): Promise<IResultData<IKakaoPayBookInfo>> {
      const requestKakaoPayResult: IResultData<IKakaoPayBookInfo> = {
        isSuccess: false,
      };

      await bookRepository()
        .requestKakaoPay(
          autoSeatAssgin,
          seatsSelectable,
          turnId,
          getOnStationId,
          getOffStationId,
          dateInfos,
          isMonthlyBook
        )
        .then((result) => {
          result
            .map((result) => {
              requestKakaoPayResult.isSuccess = true;
              requestKakaoPayResult.data = result;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(requestKakaoPayResult, error);
              requestKakaoPayResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          requestKakaoPayResult.errorMessage = error.message;
        });

      return requestKakaoPayResult;
    },

    async failKakaoPay(
      payUid: string,
      bookUid: string
    ): Promise<IResultData<boolean>> {
      const failKakaoPayResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bookRepository()
        .failKakaoPay(payUid, bookUid)
        .then((result) => {
          result
            .map((resData) => {
              failKakaoPayResult.isSuccess = resData.isSuccess;
              failKakaoPayResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(failKakaoPayResult, error);
              failKakaoPayResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          failKakaoPayResult.errorMessage = error.message;
        });

      return failKakaoPayResult;
    },

    async approveKakaoPay(
      payUid: string,
      pgToken: string
    ): Promise<IResultData<boolean>> {
      const approveKakaoPayResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bookRepository()
        .approveKakaoPay(payUid, pgToken)
        .then((result) => {
          result
            .map((resData) => {
              approveKakaoPayResult.isSuccess = resData.isSuccess;
              approveKakaoPayResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(approveKakaoPayResult, error);
              approveKakaoPayResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          approveKakaoPayResult.errorMessage = error.message;
        });

      return approveKakaoPayResult;
    },

    async cancelKakaoPay(
      bookUid: string,
      bookDetailUid?: string
    ): Promise<IResultData<boolean>> {
      const cancelKakaoPayResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bookRepository()
        .cancelKakaoPay(bookUid, bookDetailUid)
        .then((result) => {
          result
            .map((resData) => {
              cancelKakaoPayResult.isSuccess = resData.isSuccess;
              cancelKakaoPayResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(cancelKakaoPayResult, error);
              cancelKakaoPayResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          cancelKakaoPayResult.errorMessage = error.message;
        });

      return cancelKakaoPayResult;
    },

    async failKcp(
      payUid: string,
      bookUid: string
    ): Promise<IResultData<boolean>> {
      const failKcpResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bookRepository()
        .failKcp(payUid, bookUid)
        .then((result) => {
          result
            .map((resData) => {
              failKcpResult.isSuccess = resData.isSuccess;
              failKcpResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(failKcpResult, error);
              failKcpResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          failKcpResult.errorMessage = error.message;
        });

      return failKcpResult;
    },

    /**
     * 무료 임시 예약 요청 API
     * 무료 임시 예약 후 무료 예약 확정 API를 반드시 호출해야 예약이 완료됨
     * @param autoSeatAssgin 선택한 좌석이 선점되어 있을때 다른 좌석으로 자동 배석할지 여부
     * @param turnId 노선 회차 식별자
     * @param getOnStationId 승차 정류장 식별자
     * @param getOffStationId 하차 정류장 식별자
     * @param dateInfos 탑승일자 정보 리스트
     */
    async requestTempBook(
      autoSeatAssgin: boolean,
      seatsSelectable: boolean,
      turnId: string,
      getOnStationId: string,
      getOffStationId: string,
      dateInfos: IDateInfo[],
      isMonthlyBook: boolean
    ): Promise<IResultData<ITempBookResult>> {
      const bookTempResult: IResultData<ITempBookResult> = {
        isSuccess: false,
      };

      await bookRepository()
        .requestTempBook(
          autoSeatAssgin,
          seatsSelectable,
          turnId,
          getOnStationId,
          getOffStationId,
          dateInfos,
          isMonthlyBook
        )
        .then((result) => {
          result
            .map((resData) => {
              bookTempResult.isSuccess = true;
              bookTempResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(bookTempResult, error);
              bookTempResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          bookTempResult.errorMessage = error.message;
        });

      return bookTempResult;
    },

    /**
     * 무료 예약 확정 API
     * @param bookUid 예약 식별자
     */
    async requestApproveBook(bookUid: string): Promise<IResultData<boolean>> {
      const bookApproveResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bookRepository()
        .requestApproveBook(bookUid)
        .then((result) => {
          result
            .map((resData) => {
              bookApproveResult.isSuccess = resData.isSuccess;
              bookApproveResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(bookApproveResult, error);
              bookApproveResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          bookApproveResult.errorMessage = error.message;
        });

      return bookApproveResult;
    },

    /**
     * 무료 예약 취소 API
     * @param bookId 예약 식별자
     * @param bookDetailId 예약 상세 식별자
     */
    async requestCancelBook(
      bookId: string,
      bookDetailId: string
    ): Promise<IResultData<boolean>> {
      const requestCancelBookResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bookRepository()
        .requestCancelBook(bookId, bookDetailId)
        .then((result) => {
          result
            .map((resData) => {
              requestCancelBookResult.isSuccess = resData.isSuccess;
              requestCancelBookResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(requestCancelBookResult, error);
              requestCancelBookResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          requestCancelBookResult.errorMessage = error.message;
        });

      return requestCancelBookResult;
    },

    /**
     * 무료 예약 실패 API
     * @param bookId 예약 식별자
     */
    async requestFailBook(bookId: string): Promise<IResultData<boolean>> {
      const requestFailBookResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bookRepository()
        .requestFailBook(bookId)
        .then((result) => {
          result
            .map((resData) => {
              requestFailBookResult.isSuccess = resData.isSuccess;
              requestFailBookResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(requestFailBookResult, error);
              requestFailBookResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          requestFailBookResult.errorMessage = error.message;
        });

      return requestFailBookResult;
    },
  },
});
